import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  Alert,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Cloud as CloudIcon,
  Settings as SettingsIcon,
  PersonOutline as ManualIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { updateIntake, saveIntakeAsync, selectOnboarding } from '../../../store/onboardingSlice';
import { IntakeData } from '../../../api/marketing';

interface PhoneConfigStepProps {
  onNext: () => void;
  onPrev: () => void;
}

interface ProviderOption {
  value: string;
  label: string;
  description: string;
  icon: ReactNode;
}

const providerOptions: ProviderOption[] = [
  {
    value: 'twilio',
    label: 'Twilio',
    description: 'Most popular. Bring your existing Twilio account.',
    icon: <CloudIcon />,
  },
  {
    value: 'telnyx',
    label: 'Telnyx',
    description: 'Enterprise-grade SIP trunking and messaging.',
    icon: <CloudIcon />,
  },
  {
    value: 'custom_sip',
    label: 'Custom SIP',
    description: 'Connect your own SIP trunk or PBX.',
    icon: <SettingsIcon />,
  },
  {
    value: 'manual',
    label: 'Manual / No Phone',
    description: "I'll configure phone later or use external phones.",
    icon: <ManualIcon />,
  },
];

const PhoneConfigStep: FC<PhoneConfigStepProps> = ({ onNext, onPrev }) => {
  const dispatch = useAppDispatch();
  const { intake } = useAppSelector(selectOnboarding);

  // Check if VoIP was enabled in integrations
  const voipEnabled = intake.featuresRequested?.voip ?? true;

  const [provider, setProvider] = useState<string>(
    intake.phoneProvider || (voipEnabled ? 'twilio' : 'manual')
  );

  const { control, handleSubmit, watch } = useForm<Partial<IntakeData>>({
    defaultValues: {
      phoneConfig: intake.phoneConfig || {},
    },
  });

  const formValues = watch();

  // Auto-save on change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(updateIntake({
        phoneProvider: provider as IntakeData['phoneProvider'],
        phoneConfig: formValues.phoneConfig,
      }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [provider, formValues.phoneConfig, dispatch]);

  const onSubmit = async (data: Partial<IntakeData>) => {
    const phoneData = {
      phoneProvider: provider as IntakeData['phoneProvider'],
      phoneConfig: data.phoneConfig,
    };
    dispatch(updateIntake(phoneData));
    await dispatch(saveIntakeAsync({ ...intake, ...phoneData, currentStep: 5 }));
    onNext();
  };

  if (!voipEnabled) {
    return (
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Phone Configuration
        </Typography>
        <Alert severity="info" sx={{ mb: 4 }}>
          You didn't select VoIP integration in the previous step.
          You can skip this step or go back to enable VoIP.
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={onPrev}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
          >
            Skip & Continue
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Phone Configuration
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Configure your VoIP phone integration. You can bring your own provider or configure later.
      </Typography>

      {/* Provider Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Select Phone Provider
        </Typography>
        <RadioGroup
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
        >
          <Grid container spacing={2}>
            {providerOptions.map((option) => (
              <Grid item xs={12} md={6} key={option.value}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    borderColor: provider === option.value ? 'primary.main' : 'divider',
                    bgcolor: provider === option.value ? 'rgba(100, 181, 246, 0.08)' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={() => setProvider(option.value)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Radio
                      value={option.value}
                      checked={provider === option.value}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: provider === option.value ? 'primary.main' : 'action.hover',
                        color: provider === option.value ? 'white' : 'text.secondary',
                      }}
                    >
                      {option.icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {option.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
      </Box>

      {/* Provider-specific configuration */}
      {provider === 'twilio' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Twilio Configuration
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter your Twilio credentials. These will be encrypted and stored securely.
            You can also configure this later in settings.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="phoneConfig.accountSid"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Account SID"
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="phoneConfig.authToken"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="password"
                    label="Auth Token"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {provider === 'telnyx' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Telnyx Configuration
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter your Telnyx API key. You can also configure this later in settings.
          </Alert>
          <Controller
            name="phoneConfig.apiKey"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type="password"
                label="API Key"
              />
            )}
          />
        </Box>
      )}

      {provider === 'custom_sip' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Custom SIP Configuration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="phoneConfig.sipHost"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="SIP Server Host"
                    placeholder="sip.yourpbx.com"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="phoneConfig.sipPort"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="SIP Port"
                    placeholder="5060"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="phoneConfig.sipUsername"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="SIP Username"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="phoneConfig.sipPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="password"
                    label="SIP Password"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {provider === 'manual' && (
        <Alert severity="info" sx={{ mb: 4 }}>
          You can configure phone integration later in your Debtornator settings.
          Agents will need to use external phones until VoIP is configured.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={onPrev}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
        >
          Continue to Review
        </Button>
      </Box>
    </Box>
  );
};

export default PhoneConfigStep;
